import {
  AgentThread,
  getThreads,
} from "@ui/agent/components/agent/api/chat-api";
import { useCallback, useRef } from "react";

import { useChatsContext } from "@/lib/context/ChatsProvider/hooks/useChatsContext";
import { useAxios } from "@/lib/hooks";
import { mapThreadStatus, ThreadEntity } from "@/lib/types/Thread";
import { asUtc } from "@/lib/utils/date";

const PAGE_SIZE = 20;

const mapThread = (thread: AgentThread): ThreadEntity => ({
  chat_id: thread.id,
  chat_name: thread.name,
  creation_time: asUtc(thread.created_at),
  chat_type: "agent",
  agent_id: thread.agent_id,
  assistant_id: thread.assistant_id,
  is_favorite: thread.is_favorite,
  last_message_at: thread.last_message_at,
  last_read_at: thread.last_read_at,
  status: mapThreadStatus(thread.status),
});

export const useFetchAgentThreads = () => {
  const { axiosInstance } = useAxios();
  const {
    setAllChats,
    appendChats,
    setIsLoading,
    setHasMore,
    setStatusCounts,
  } = useChatsContext();
  const offsetRef = useRef(0);
  const isFetchingRef = useRef(false);

  const fetchThreads = useCallback(
    async (search?: string, isFavorite?: boolean, reset?: boolean) => {
      setIsLoading(true);
      const loadCount = reset
        ? PAGE_SIZE
        : Math.max(PAGE_SIZE, offsetRef.current);
      try {
        const { items, total, status_counts } = await getThreads(axiosInstance, {
          search,
          isFavorite,
          offset: 0,
          limit: loadCount,
        });
        const mappedThreads = items.map(mapThread);
        setAllChats(mappedThreads);
        offsetRef.current = mappedThreads.length;
        setHasMore(offsetRef.current < total);
        setStatusCounts(status_counts);
      } catch (error) {
        console.error("Failed to fetch agent threads:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [axiosInstance, setAllChats, setHasMore, setIsLoading, setStatusCounts],
  );

  const fetchMoreThreads = useCallback(
    async (search?: string, isFavorite?: boolean) => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;
      setIsLoading(true);
      try {
        const { items, total, status_counts } = await getThreads(axiosInstance, {
          search,
          isFavorite,
          offset: offsetRef.current,
          limit: PAGE_SIZE,
        });
        const mappedThreads = items.map(mapThread);
        appendChats(mappedThreads);
        offsetRef.current += mappedThreads.length;
        setHasMore(offsetRef.current < total);
        setStatusCounts(status_counts);
      } catch (error) {
        console.error("Failed to fetch more agent threads:", error);
      } finally {
        setIsLoading(false);
        isFetchingRef.current = false;
      }
    },
    [appendChats, axiosInstance, setHasMore, setIsLoading, setStatusCounts],
  );

  return { fetchThreads, fetchMoreThreads };
};
